// Generador de menú semanal — 7 platos variados según el tipo elegido.
// Se ejecuta 100% client-side sobre el recipes.json ya cargado.
//
// Tipos:
//   - normal:   mezcla balanceada (2 carnes, 1 pescado, 2 caseros/veg, 1 pasta/arroz, 1 varía)
//   - light:    prioriza ensaladas, pescados, sopas, verduras — descarta fritos y postres
//   - especial: prioriza platos largos, guisos, asados, tradicionales

const DAYS = ["lunes","martes","miércoles","jueves","viernes","sábado","domingo"];
const DAY_LABELS = ["Lunes","Martes","Miércoles","Jueves","Viernes","Sábado","Domingo"];

// Categorías que pueden ser plato principal en un menú semanal
const MAIN_DISH_CATEGORIES = new Set([
  "Carnes y Pollos",
  "Pescados y Mariscos",
  "Platos Caseros y Vegetarianos",
  "Platos Internacionales",
  "Panes, Empanadas y Fritos",
  "Guarniciones y Arroces",
  "Entradas, Aperitivos y Sopas",
  "Festividades Judías",
]);

const LIGHT_KEYWORDS = /ensalada|pescado|plancha|liviano|light|vegan|sopa|caldo|verduras|integral|quinoa|ceviche|tartare|carpaccio/i;
const HEAVY_KEYWORDS = /frito|empanada|hojaldre|torta|helado|churro|dulce/i;
const ESPECIAL_KEYWORDS = /asado|guiso|larg|braseado|tradicional|festivo|fin de semana|domingo|celebra|osso ?buco|cazuela|pastel|carbonada|pernil|risotto/i;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function textOf(r) {
  return `${r.nombre || ""} ${r.categoria || ""} ${(r.tags || []).join(" ")} ${r.descripcion_corta || ""} ${r.tiempo_coccion || ""}`;
}

function scoreLight(r) {
  const text = textOf(r);
  let s = 0;
  if (LIGHT_KEYWORDS.test(text)) s += 3;
  if (HEAVY_KEYWORDS.test(text)) s -= 4;
  if (r.categoria === "Pescados y Mariscos") s += 2;
  if (r.categoria === "Entradas, Aperitivos y Sopas") s += 2;
  if (r.categoria === "Platos Caseros y Vegetarianos") s += 1;
  if (r.categoria === "Panes, Empanadas y Fritos") s -= 3;
  return s;
}

function scoreEspecial(r) {
  const text = textOf(r);
  let s = 0;
  if (ESPECIAL_KEYWORDS.test(text)) s += 3;
  if (r.categoria === "Carnes y Pollos") s += 2;
  if (r.categoria === "Festividades Judías") s += 2;
  if (r.categoria === "Platos Internacionales") s += 1;
  return s;
}

// Selecciona 7 recetas evitando categoría repetida en días seguidos.
function selectWithVariety(sortedPool, count = 7) {
  const chosen = [];
  const usedSlugs = new Set();
  const pool = [...sortedPool];
  while (chosen.length < count && pool.length > 0) {
    const lastCat = chosen.length > 0 ? chosen[chosen.length - 1].categoria : null;
    // Busca la primera candidata que no repita categoría
    let idx = pool.findIndex((r) => !usedSlugs.has(r.slug) && r.categoria !== lastCat);
    if (idx === -1) {
      // Ya no hay opciones sin repetir cat — tomamos la primera disponible
      idx = pool.findIndex((r) => !usedSlugs.has(r.slug));
    }
    if (idx === -1) break;
    const r = pool.splice(idx, 1)[0];
    usedSlugs.add(r.slug);
    chosen.push(r);
  }
  return chosen;
}

/**
 * @param {Array} allRecipes - recetas cargadas del JSON
 * @param {"normal"|"light"|"especial"} type
 * @param {Array<string>} excludeSlugs - recetas a excluir (ya usadas)
 * @param {Date} startDate - fecha del primer día (por defecto hoy)
 */
export function generateWeeklyMenu(allRecipes, type = "normal", excludeSlugs = [], startDate = null) {
  const excludeSet = new Set(excludeSlugs);
  const eligible = (allRecipes || []).filter((r) =>
    MAIN_DISH_CATEGORIES.has(r.categoria) && !excludeSet.has(r.slug)
  );

  let sorted;
  if (type === "light") {
    sorted = eligible.map((r) => ({ r, s: scoreLight(r) + Math.random() * 0.5 }))
      .filter((x) => x.s > -1)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.r);
  } else if (type === "especial") {
    sorted = eligible.map((r) => ({ r, s: scoreEspecial(r) + Math.random() * 0.5 }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.r);
    // Si el pool "especial" es muy chico (< 15), agrega recetas normales al final
    if (sorted.length < 20) {
      const extra = shuffle(eligible.filter((r) => !sorted.some((x) => x.slug === r.slug)));
      sorted = [...sorted, ...extra];
    }
  } else {
    // Normal: pool completo barajado
    sorted = shuffle(eligible);
  }

  // Tomamos los top 40 y seleccionamos 7 con variedad
  const chosen = selectWithVariety(sorted.slice(0, Math.max(40, 7)), 7);

  // Mapea a días (arranca por la fecha dada o hoy)
  const start = startDate ? new Date(startDate) : new Date();
  start.setHours(0, 0, 0, 0);
  return chosen.map((r, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    // JS: 0=domingo. Convertimos a 0=lunes..6=domingo
    const jsIdx = (d.getDay() + 6) % 7;
    return {
      day_index: jsIdx,
      day: DAYS[jsIdx],
      day_label: DAY_LABELS[jsIdx],
      date: d.toISOString().split("T")[0],
      slug: r.slug,
      nombre: r.nombre,
      categoria: r.categoria,
      photo: r.photo || null,
      tiempo_preparacion: r.tiempo_preparacion || "",
    };
  });
}

// Reemplaza UN día del menú por otra receta del pool (respetando exclusiones).
export function regenerateOneDay(dayIndex, currentMenu, allRecipes, type = "normal") {
  const excludeSlugs = currentMenu.map((d, i) => i === dayIndex ? null : d.slug).filter(Boolean);
  const generated = generateWeeklyMenu(allRecipes, type, excludeSlugs);
  // Toma la primera candidata que NO sea la actual (para que se note el cambio)
  const oldSlug = currentMenu[dayIndex]?.slug;
  const candidate = generated.find((x) => x.slug !== oldSlug) || generated[0];
  if (!candidate) return currentMenu;
  const next = [...currentMenu];
  next[dayIndex] = {
    ...next[dayIndex],
    slug: candidate.slug,
    nombre: candidate.nombre,
    categoria: candidate.categoria,
    photo: candidate.photo,
    tiempo_preparacion: candidate.tiempo_preparacion,
  };
  return next;
}

// ============================================================
// LISTA DE COMPRAS AGREGADA POR CATEGORÍA + CANTIDADES SUMADAS
// ============================================================

const SHOP_SECTIONS = [
  { name: "Verdulería y frutas", kw: /palta|tomate|cebolla|ajo|lechuga|choclo|zapallo|zanahoria|pimentón|pimiento|papa|papas|apio|cilantro|perejil|albahaca|ciboulette|jeng[ie]bre|champi[ñn]on|esp[ai]nac|brócoli|brocoli|coliflor|repollo|pepino|rabanito|betarraga|berenjena|zucchini|porotos\s*verdes|arveja|maíz|maiz|hongos|lim[oó]n|naranja|manzana|pl[aá]tano|pera|frutilla|frambuesa|ar[aá]ndano|kiwi|uva|durazno|damasco|cereza|sand[ií]a|mel[oó]n|piña|mango/i },
  { name: "Carnicería y pescadería", kw: /carne|posta|asado|lomo|filete|pollo|pavo|cerdo|chuleta|costilla|molida|molido|mechada|pernil|tocino|panceta|jam[oó]n|salchicha|chorizo|salm[oó]n|merluza|reineta|congrio|atún|atun|camar[oó]n|jaiba|pulpo|calamar|machas|locos|ostiones|mariscos|pescado|cordero|ganso|pato/i },
  { name: "Lácteos y huevos", kw: /leche|crema|yog[uú]r|queso|mantequilla|margarina|huevo|huevos|ricotta|mozzarella|parmesano|provolone|gouda|cheddar|quark/i },
  { name: "Panadería y harinas", kw: /harina|pan\b|pan rallado|migas|hojaldre|masa\s*(filo|brisa|hojaldre)|tortilla|levadura|tort[ií]n/i },
  { name: "Despensa", kw: /az[uú]car|sal\b|aceite|vinagre|salsa|mostaza|mayonesa|kétchup|miel|chocolate|cacao|coco|nueces|almendras|man[ií]|pistacho|pasas|d[aá]tiles|arroz|fideos|pasta|s[eé]mola|polenta|quinoa|lentejas|garbanzos|porotos|tofu|caldo|cubo|t[eé]\b|caf[eé]|gelatina|maicena|polvo de hornear|bicarbonato/i },
  { name: "Especias y condimentos", kw: /pimienta|comino|or[eé]gano|tomillo|romero|laurel|paprika|p[aá]prika|merk[eé]n|aj[ií]\b|canela|nuez moscada|cardamomo|curry|cúrcuma|curcuma|s[eé]samo|sesamo|amapola|chili|cayena|achiote|piment[oó]n ahum/i },
  { name: "Bebidas y vinos", kw: /vino|cerveza|champ[aá]?gne|espumante|whisky|ron|vodka|pisco|jugo|bebida|coca[\s-]?cola|sprite|agua mineral|leche\s*de\s*coco|leche\s*de\s*almendra/i },
  { name: "Congelados y otros", kw: /helado|hielo|congelad/i },
];
const SECTION_ORDER = [...SHOP_SECTIONS.map((s) => s.name), "Otros"];

function classifySection(text) {
  for (const sec of SHOP_SECTIONS) if (sec.kw.test(text)) return sec.name;
  return "Otros";
}

// Parsea cantidad + unidad + resto. Ej: "2 cebollas grandes" -> {qty: 2, unit: "u", base: "cebollas grandes"}
// "1,5 kg de pollo" -> {qty: 1.5, unit: "kg", base: "pollo"}
// "1/2 taza de aceite" -> {qty: 0.5, unit: "taza", base: "aceite"}
const UNIT_RE = /^\s*(\d+(?:[.,]\d+)?|\d+\/\d+)\s*(kg|kgs|g|gr|grs|gramos|ml|cc|l|lt|litros?|tazas?|cucharadas?|cdtas?|cdas?|cditas?|cucharadit?as?|unidad(?:es)?|paquetes?|latas?|frascos?|dientes?|hojas?|ramas?|pizcas?|manojos?|racimos?|atados?|bandejas?)?\s*(?:de\s+)?(.+)$/i;

function parseFraction(s) {
  if (!s) return null;
  s = s.replace(",", ".");
  if (s.includes("/")) {
    const [a, b] = s.split("/").map(Number);
    if (b && !isNaN(a) && !isNaN(b)) return a / b;
  }
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

// Normaliza unidad a canonical
function canonicalUnit(u) {
  if (!u) return "u";
  const l = u.toLowerCase();
  if (/^kg|kgs$/.test(l)) return "kg";
  if (/^g|gr|grs|gramos$/.test(l)) return "g";
  if (/^ml|cc$/.test(l)) return "ml";
  if (/^l|lt|litros?$/.test(l)) return "l";
  if (/^tazas?$/.test(l)) return "taza";
  if (/^cucharadas?|cdas?$/.test(l)) return "cda";
  if (/^cdtas?|cditas?|cucharadit?as?$/.test(l)) return "cdta";
  if (/^unidad(es)?$/.test(l)) return "u";
  if (/^paquetes?$/.test(l)) return "paquete";
  if (/^latas?$/.test(l)) return "lata";
  if (/^frascos?$/.test(l)) return "frasco";
  if (/^dientes?$/.test(l)) return "diente";
  if (/^hojas?$/.test(l)) return "hoja";
  if (/^ramas?$/.test(l)) return "rama";
  if (/^pizcas?$/.test(l)) return "pizca";
  if (/^manojos?$/.test(l)) return "manojo";
  if (/^racimos?$/.test(l)) return "racimo";
  if (/^atados?$/.test(l)) return "atado";
  if (/^bandejas?$/.test(l)) return "bandeja";
  return l;
}

function parseIngredient(text) {
  const s = String(text || "").trim();
  const m = s.match(UNIT_RE);
  if (m) {
    const qty = parseFraction(m[1]);
    const unit = canonicalUnit(m[2] || "");
    const base = (m[3] || "").trim().toLowerCase();
    return { qty, unit, base, original: s };
  }
  // Sin cantidad detectada
  return { qty: null, unit: null, base: s.toLowerCase(), original: s };
}

// Clave para agrupar: unidad canónica + primer sustantivo del base (después de a-adj)
function groupKey(parsed) {
  const base = parsed.base
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\wñ ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  // Toma las primeras 2 palabras significativas
  const words = base.split(" ").filter((w) => w.length >= 3 && !["del","los","las","con","sin","que","muy","o"].includes(w));
  const key = words.slice(0, 2).join(" ") || base;
  return `${parsed.unit || "u"}::${key}`;
}

/**
 * Construye la lista de compras a partir de un array de recetas embebidas.
 * @param {Array} recipesFull - recetas completas (con ingredientes)
 * @returns {{sections: Array<{name: string, items: Array}>}}
 */
export function buildShoppingList(recipesFull) {
  const groups = new Map();
  for (const r of recipesFull || []) {
    for (const raw of (r.ingredientes || [])) {
      const text = String(raw || "").trim();
      if (!text || text.endsWith(":")) continue;
      const parsed = parseIngredient(text);
      const key = groupKey(parsed);
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          section: classifySection(text),
          items: [],   // ocurrencias originales por receta
          totals: {},  // qty por unit
          display_base: parsed.base,
        });
      }
      const g = groups.get(key);
      g.items.push({ text, recipe_slug: r.slug, recipe_nombre: r.nombre, qty: parsed.qty, unit: parsed.unit });
      if (parsed.qty != null) {
        const u = parsed.unit || "u";
        g.totals[u] = (g.totals[u] || 0) + parsed.qty;
      }
    }
  }

  // Construimos texto de resumen por grupo
  const rows = [];
  for (const g of groups.values()) {
    const totalTexts = Object.entries(g.totals).map(([u, q]) => {
      const qStr = Number.isInteger(q) ? String(q) : q.toFixed(2).replace(/\.?0+$/, "");
      const unitLabel = u === "u" ? "" : ` ${u}`;
      return `${qStr}${unitLabel}`;
    });
    const summary = totalTexts.length
      ? `${totalTexts.join(" + ")} de ${g.display_base}`
      : g.display_base;
    rows.push({
      key: g.key,
      section: g.section,
      summary,
      count_recipes: new Set(g.items.map((i) => i.recipe_slug)).size,
      items: g.items,
    });
  }

  // Agrupa por sección respetando el orden canónico
  const bySection = new Map();
  for (const row of rows) {
    if (!bySection.has(row.section)) bySection.set(row.section, []);
    bySection.get(row.section).push(row);
  }
  // Ordena items alfabéticamente dentro de cada sección
  for (const list of bySection.values()) {
    list.sort((a, b) => a.summary.localeCompare(b.summary, "es"));
  }
  const sections = SECTION_ORDER
    .filter((n) => bySection.has(n))
    .map((n) => ({ name: n, items: bySection.get(n) }));
  return { sections };
}

// ============================================================
// PERSISTENCIA LOCAL POR MIEMBRO (localStorage)
// ============================================================
const LS_KEY = "recetario_weekly_menu_";

export function loadWeeklyMenu(member) {
  try {
    const raw = localStorage.getItem(LS_KEY + member);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function saveWeeklyMenu(member, data) {
  try { localStorage.setItem(LS_KEY + member, JSON.stringify(data)); } catch { /* silent */ }
}
export function clearWeeklyMenu(member) {
  try { localStorage.removeItem(LS_KEY + member); } catch { /* silent */ }
}

// Estado de check-off "ya tengo" en la lista de compras (persistente por miembro)
const CHECK_KEY = "recetario_weekly_checked_";
export function loadChecked(member) {
  try {
    const raw = localStorage.getItem(CHECK_KEY + member);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
export function saveChecked(member, checked) {
  try { localStorage.setItem(CHECK_KEY + member, JSON.stringify(checked)); } catch { /* silent */ }
}

export { DAYS, DAY_LABELS };
